import { atom } from 'jotai';

import type {
  AuthSessionMetadata,
  AuthState,
  AuthUnauthenticatedReason,
  KeycloakSessionBridge,
  KindeDesktopBridge,
} from '../contracts';

/**
 * Coarse-grained auth state. Initial value is `'loading'` so the app can
 * render a skeleton until the bootstrap prewarm resolves on first mount.
 */
export const authStateAtom = atom<AuthState>('loading');

/**
 * Why the cache transitioned to `'unauthenticated'`. The axios 401-retry
 * handler and the session-invalidation telemetry subscriber both gate on
 * this — `'session_invalid'` triggers telemetry, `'explicit_logout'` does
 * not, and `null` (bare-null branch or boot-time) is operationally neutral.
 */
export const authUnauthenticatedReasonAtom = atom<AuthUnauthenticatedReason>(null);

/**
 * Reactive mirror of `cached.session`. Consumers that need user / userRefId
 * / kindeUserId / region read this instead of subscribing to the cache
 * directly.
 */
export const authSessionMetadataAtom = atom<AuthSessionMetadata | null>(null);

/**
 * `true` while the most recent cache fetch failed transiently (timeout,
 * network, parse, unrecognised exception). Read by the axios 401-retry
 * handler to suppress a logout when the cache is in a transient-failure
 * state — the BE 401 doesn't mean the session is dead, just that the
 * cache couldn't confirm the token is fresh.
 */
export const authTransientFailureAtom = atom<boolean>(false);

/**
 * Bridge from `useKinde().getToken` into module-level code. Populated by
 * a bridge component that lives near the app root and observes
 * `useKinde`. The cache's Kinde-desktop branch reads this atom; `null`
 * means the bridge hasn't mounted yet (the branch returns `null`,
 * mapping to the bare-null outcome).
 */
export const kindeDesktopBridgeAtom = atom<KindeDesktopBridge | null>(null);

/**
 * When `true`, `getAccessToken()` returns `null` at entry — before any
 * fetch, before any atom transition — regardless of options
 * (`forceRefresh: true` included). One gate covers every consumer: the
 * scheduler's prewarm and focus refetch, the axios read path, and any
 * direct caller. The mirror-write is inherently covered because no
 * success outcome can exist while suppressed.
 *
 * Default `false`. The only current setter is the `/chat/embed` page,
 * which enforces public/unauthenticated UI even when the parent app
 * holds a same-origin session cookie. Suppression is page-lifetime
 * state: the setter MUST reset it to `false` on unmount — a leaked
 * `true` leaves the app-wide cache dead across client-side navigation
 * until a full reload.
 *
 * Deliberately NOT modelled as a bare-null cache classification. A
 * bare-null return goes through the four-outcome pipeline and writes
 * atoms; the suppression gate must NOT write anything (that would
 * pollute the suppressed context's atoms and defeat the entire point).
 */
export const authCacheSuppressedAtom = atom<boolean>(false);

/**
 * Bridge from NextAuth's `useSession().update` (Keycloak web) into
 * module-level code. The cache's Keycloak-web force-refresh branch
 * reads this atom and calls `update({ refreshNow: true })`. The
 * non-force branch does NOT depend on the bridge — it fetches
 * `/api/auth/session` directly. Populated by a bridge component
 * shipped in the `add-keycloak-session-refresh` sibling.
 */
export const keycloakSessionBridgeAtom = atom<KeycloakSessionBridge | null>(null);
